import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CountrySelector } from './CountrySelector';

interface MobileMenuProps {
  handleLogout: () => void;
  setIsMobileMenuOpen: (isOpen: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  handleSearch: (e: React.FormEvent) => void;
  mainHubs: Array<{ id: string; name: string; path: string }>;
}

export function MobileMenu({
  handleLogout,
  setIsMobileMenuOpen,
  searchQuery,
  setSearchQuery,
  handleSearch,
  mainHubs,
}: MobileMenuProps) {
  const { user, isAdmin, isSupport, isFounder, openAuthModal } = useAuth();

  return (
    <div id="mobile-navigation" className="md:hidden bg-white border-b border-gray-100 shadow-xl">
      <div className="px-4 py-4 space-y-4">
        {/* Marketplace Country Selector Row */}
        <div className="flex items-center justify-between px-3.5 py-2.5 bg-slate-50 rounded-xl border border-slate-100">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">
            Region
          </span>
          <CountrySelector />
        </div>



        <div
          className="flex overflow-x-auto gap-2 pb-2 -mx-4 px-4 snap-x"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <style>{`.hide-scroll::-webkit-scrollbar { display: none; }`}</style>
          <div className="flex gap-2 hide-scroll">
            {mainHubs.map((hub) => (
              <Link
                key={hub.id}
                to={hub.path}
                className="shrink-0 snap-start rounded-full border border-gray-200 bg-gray-50 px-4 py-2 text-[11px] font-black uppercase tracking-tight text-slate-700 hover:border-orange-300 hover:text-[#FF5722]"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {hub.name}
              </Link>
            ))}
          </div>
        </div>

        {user && (
          <div className="pt-4 mt-2 border-t border-gray-100">
            <div className="px-2 pb-3 mb-2 border-b border-gray-50">
              <p className="text-sm font-black text-slate-800 truncate">
                {user.displayName || (isAdmin || isFounder ? 'Admin Account' : 'Seller Account')}
              </p>
              <p className="text-xs font-semibold text-slate-500 truncate">{user.email}</p>
            </div>
            <div className="grid grid-cols-1 gap-1">
              <Link
                to="/dashboard?tab=profile"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">👤</span> Profile
              </Link>
              <Link
                to="/dashboard"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">🏪</span> My Shop
              </Link>
              <Link
                to="/dashboard?tab=listings"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">📦</span> My Ads
              </Link>
              <Link
                to="/messages"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">💬</span> Messages
              </Link>
              <Link
                to="/notifications"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">🔔</span> Notifications
              </Link>
              <Link
                to="/saved"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">❤️</span> Saved Ads
              </Link>
              <Link
                to="/dashboard?tab=analytics"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">📊</span> Performance
              </Link>
              <Link
                to="/settings"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">⚙️</span> Settings
              </Link>
              <Link
                to="/help-center"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">❓</span> Help & Support
              </Link>
              
              {/* Admin/Founder link */}
              {(isAdmin || isSupport || isFounder) && (
                <Link
                  to="/admin"
                  className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <span className="text-lg">🛡️</span> Admin Control
                </Link>
              )}
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  handleLogout();
                }}
                className="w-full text-left flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-red-500 hover:bg-red-50 rounded-xl transition-colors"
              >
                <span className="text-lg">🚪</span> Logout
              </button>
            </div>
          </div>
        )}

        {!user && (
          <div className="pt-4 mt-2 border-t border-gray-100">
            <div className="px-2 pb-3 mb-2 border-b border-gray-50 flex flex-col gap-2">
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  openAuthModal('login');
                }}
                className="w-full text-center bg-[#FF6600] text-white py-2.5 rounded-xl font-bold hover:bg-[#E65C00] transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  openAuthModal('signup');
                }}
                className="w-full text-center bg-orange-50 text-orange-700 py-2.5 rounded-xl font-bold hover:bg-orange-100 transition-colors"
              >
                Sign Up
              </button>
            </div>
            <div className="grid grid-cols-1 gap-1">
              <Link
                to="/help-center"
                className="flex items-center gap-3 px-4 py-1.5 text-sm font-bold text-slate-600 hover:bg-orange-50 hover:text-[#FF6600] rounded-xl transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span className="text-lg">❓</span> Help & Support
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
